// ---------------------------------------------------------------------------
// Como o Foco estava APRESENTADO — em tela cheia ou no drawer minimizado.
//
// ## Por que existe (BUG-222)
//
// No app das lojas, voltar do bloqueio na Home ou no Perfil próprio disparava a
// recriação PREVENTIVA da WebView — a cura do BUG-197 (da 1.8 à 1.11; saiu em
// 28/09, e hoje só o batimento recria, quando o JS para). Isso não é um
// "resume": é um BOOT de JS completo. O React remonta do zero, e o
// `FocusSessionProvider` recupera a sessão do servidor pela porta da
// recuperação, que sempre abriu MINIMIZADA (invariante 17 ④ da skill).
//
// Resultado medido no relato: quem estava lendo em TELA CHEIA voltava para a
// Home com uma faixa de 116px no rodapé. Do ponto de vista de quem usa, o app
// "saiu do modo foco" sozinho — e ela não tinha pedido nada.
//
// ## ⚠️ Isto NÃO revoga o invariante 17 ④ — restringe
//
// "A recuperação abre minimizada" segue sendo o DEFAULT, e segue valendo para
// toda sessão que a pessoa não estava vendo em tela cheia. A razão registrada
// para ele ("quem trocou de aba não pediu a prisão de volta na cara") continua
// honrada: quem minimizou tem o marcador em `false` e continua recebendo o
// drawer.
//
// O que muda é que a apresentação deixa de ser DESCARTADA. Hoje o app não
// consegue distinguir "ela minimizou" de "o app recarregou por baixo dela" —
// as duas colapsam em minimizada —, e é essa perda de informação que produziu
// o relato, não a regra.
//
// ## Por que a chave é o ID DA SESSÃO, e não um prazo
//
// O marcador morre com a sessão, que já é a janela natural (teto de 2h,
// `MAX_FOCUS_DURATION_SEC`). Um prazo em minutos erraria justamente o caso
// central do produto: um pomodoro de 25 min é feito com o telefone bloqueado
// DE PROPÓSITO, então "voltou rápido" não é sinal de nada e "demorou" não é
// sinal de abandono. A identidade da sessão é o único recorte que não precisa
// de um número escolhido a dedo.
//
// ⚠️ `PENDING_SESSION_ID` é um literal COMPARTILHADO por toda sessão otimista
// (a lição do BUG-218, invariante 46): gravar sob ele faria uma sessão NOVA
// herdar a apresentação da anterior. Quem escreve pula o instante pendente, e
// aqui a leitura é sempre feita com o id que veio do servidor — um marcador
// pendente que sobrevivesse nunca casaria com nada.
//
// Falhar é sempre "não sei", e "não sei" é o comportamento de antes desta
// fatia (minimizada). Storage negado (modo privado, cookies bloqueados) nunca
// pode derrubar a sessão — mesma doutrina do `focusPrefs`.
// ---------------------------------------------------------------------------

const STORAGE_KEY = "livra_focus_presentation_v1";

interface StoredPresentation {
  sessionId: string;
  expanded: boolean;
}

/**
 * Id utilizável como chave: string não vazia. O literal pendente é barrado no
 * call site (ele é quem conhece `PENDING_SESSION_ID`); aqui o que se garante é
 * que chave vazia nunca vira marcador.
 */
function isUsableId(id: string | null | undefined): id is string {
  return typeof id === "string" && id.trim().length > 0;
}

/**
 * A sessão `sessionId` estava em TELA CHEIA quando o app parou de existir?
 *
 * `=== true` e nunca coerção: um valor lixo no storage (`1`, `"true"`) não pode
 * virar "estava expandida" — é a mesma regra do `pagesRegistered`
 * (invariante 23).
 */
export function readFocusPresentationExpanded(sessionId: string | null | undefined): boolean {
  if (!isUsableId(sessionId)) return false;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return false;
    const rec = parsed as Partial<StoredPresentation>;
    if (rec.sessionId !== sessionId) return false;
    return rec.expanded === true;
  } catch {
    // Storage negado, JSON corrompido: "não sei" → minimizada, o de antes.
    return false;
  }
}

/** Grava a apresentação vigente. Chave vazia é ignorada, nunca gravada. */
export function rememberFocusPresentation(
  sessionId: string | null | undefined,
  expanded: boolean,
): void {
  if (!isUsableId(sessionId)) return;
  try {
    const payload: StoredPresentation = { sessionId, expanded };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Sem storage a apresentação só não sobrevive ao boot — o app segue igual.
  }
}

/**
 * Esquece a apresentação. Chamado quando a sessão deixa de existir para este
 * app: o fechamento (`closeFocus`) e a recuperação que não achou sessão
 * nenhuma — o mesmo instante em que a Live Activity órfã é varrida.
 */
export function forgetFocusPresentation(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // idem
  }
}
